import { Box } from "@mui/material";
import type { ReactNode, RefObject } from "react";
import { useCallback, useMemo } from "react";
import { Page, usePageContext } from "react-pdf";
import { logError } from "../../../logging";
import type { PdfViewerState } from "./pdfViewerState";
import { getPdfJsWorkerBuild } from "./pdfjsHelper";

export interface PageContentProps {
  pageNumber: number;
  pageHeight: number | undefined;
  pageWidth: number | undefined;
  renderPage: boolean;
  renderLayers: boolean;
}

interface Props {
  viewerState: PdfViewerState;
  containerEl: HTMLDivElement | null;
  pageRefs: RefObject<HTMLElement[] | null>;
  pageHeight: number | undefined;
  pageWidth: number | undefined;
  renderPageContent?: (props: PageContentProps) => ReactNode;
}

export const PagePlaceholder = () => {
  const pageContext = usePageContext();

  const viewport = useMemo(() => {
    if (!pageContext?.page) {
      return undefined;
    }

    return pageContext.page.getViewport({ scale: pageContext.scale, rotation: pageContext.rotate });
  }, [pageContext]);

  return <Box sx={{ width: viewport?.width, height: viewport?.height }}></Box>;
};

const pageRenderThreshold = 8;

const PdfPagesContainer = ({ viewerState, containerEl, pageRefs, pageHeight, pageWidth, renderPageContent }: Props) => {
  const shouldRenderPage = useCallback(
    (pageIndex: number) =>
      pageIndex + 1 >= viewerState.currentPageNumber - pageRenderThreshold &&
      pageIndex + 1 <= viewerState.currentPageNumber + pageRenderThreshold,
    [viewerState.currentPageNumber]
  );

  const isPageVisible = useCallback(
    (pageIndex: number) => {
      const pageElementRect = pageRefs.current?.[pageIndex]?.getBoundingClientRect();
      const containerElementRect = containerEl?.getBoundingClientRect();
      return (
        !!pageElementRect &&
        !!containerElementRect &&
        pageElementRect.bottom > containerElementRect.top &&
        pageElementRect.top < containerElementRect.bottom
      );
    },
    [pageRefs, containerEl]
  );

  const handlePageError = (error: unknown, pageNumber: number, phase: string) => {
    if (!navigator.onLine) {
      return;
    }

    logError(error, "[PdfPagesContainer] page error", {
      pdfPhase: phase,
      pdfJsWorkerBuild: getPdfJsWorkerBuild(),
      pdfPageNumber: pageNumber,
    });
  };

  return (
    <Box
      sx={{
        display: "inline-flex",
        flexDirection: "column",
        alignItems: "center",
        minWidth: "100%",
        minHeight: "100%",
      }}
    >
      {viewerState.isDocumentLoaded &&
        Array.from(new Array(viewerState.numPages), (_, pageIndex) => {
          const renderPage = shouldRenderPage(pageIndex);
          const renderLayers = renderPage && isPageVisible(pageIndex);

          return (
            <Box
              key={`pdf-page-${pageIndex}`}
              // eslint-disable-next-line @eslint-react/immutability -- pageRefs is a ref object; ref callbacks may write to it
              ref={(el: HTMLElement) => {
                if (pageRefs.current) {
                  // eslint-disable-next-line @eslint-react/immutability -- see above
                  pageRefs.current[pageIndex] = el;
                } else {
                  // eslint-disable-next-line @eslint-react/immutability -- see above
                  pageRefs.current = [el];
                }
              }}
              sx={{ py: 2 }}
            >
              {renderPageContent ? (
                renderPageContent({
                  pageNumber: pageIndex + 1,
                  pageHeight,
                  pageWidth,
                  renderPage,
                  renderLayers,
                })
              ) : (
                <Page
                  pageNumber={pageIndex + 1}
                  height={pageHeight}
                  width={pageWidth}
                  customRenderer={renderPage ? undefined : PagePlaceholder}
                  renderMode={renderPage ? "canvas" : "custom"}
                  renderAnnotationLayer={renderLayers}
                  renderTextLayer={renderLayers}
                  onRenderError={(error) => handlePageError(error, pageIndex + 1, "page-render")}
                  onRenderAnnotationLayerError={(error) =>
                    handlePageError(error, pageIndex + 1, "annotation-layer-render")
                  }
                  onRenderTextLayerError={(error) => handlePageError(error, pageIndex + 1, "text-layer-render")}
                />
              )}
            </Box>
          );
        })}
    </Box>
  );
};

export default PdfPagesContainer;
